import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "admin" | "coach" | "user";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  userRoles: AppRole[];
  userRole: AppRole | null;
  viewingAs: AppRole | null;
  setViewingAs: (role: AppRole) => void;
  isAdmin: boolean;
  isCoach: boolean;
  isPlayer: boolean;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  userRoles: [],
  userRole: null,
  viewingAs: null,
  setViewingAs: () => {},
  isAdmin: false,
  isCoach: false,
  isPlayer: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [viewingAs, setViewingAs] = useState<AppRole | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user?.id) {
      setUserRoles([]);
      setViewingAs(null);
      return;
    }

    const fetchRoles = async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      if (data && data.length > 0) {
        const roles = data.map((r) => r.role as AppRole);
        setUserRoles(roles);
        // Default viewingAs to highest priority role
        const primary = roles.includes("admin") ? "admin" : roles.includes("coach") ? "coach" : "user";
        setViewingAs(primary);
        return;
      }

      // No role yet — first login. Read from user_metadata and auto-assign.
      const metaRole = (session.user.user_metadata as Record<string, unknown>)?.role;
      const wantsCoach = metaRole === "coach";

      // Always assign "user" role; additionally assign "coach" if requested
      const rolesToInsert: { user_id: string; role: AppRole }[] = [
        { user_id: session.user.id, role: "user" },
      ];
      if (wantsCoach) {
        rolesToInsert.push({ user_id: session.user.id, role: "coach" });
      }

      await supabase.from("user_roles").insert(rolesToInsert);

      if (wantsCoach) {
        await supabase.from("coach_profiles").insert({
          user_id: session.user.id,
          hourly_rate: 50,
          is_active: false,
        });
      }

      const assignedRoles = rolesToInsert.map((r) => r.role);
      setUserRoles(assignedRoles);
      setViewingAs(wantsCoach ? "coach" : "user");
    };

    fetchRoles();
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const isAdmin = userRoles.includes("admin");
  const isCoach = userRoles.includes("coach");
  const isPlayer = userRoles.includes("user");
  const userRole = isAdmin ? "admin" : isCoach ? "coach" : isPlayer ? "user" : null;

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut, userRoles, userRole, viewingAs, setViewingAs, isAdmin, isCoach, isPlayer }}>
      {children}
    </AuthContext.Provider>
  );
};
